import { useState, useEffect, useCallback } from 'react';
import {
  doc, setDoc, getDocs, collection, serverTimestamp,
  writeBatch, deleteDoc, query, where
} from 'firebase/firestore';
import { db } from '@/firebase';
import { Recipe, PlannedMealItem, ExternalGuest } from '@/types';

// Legacy ingredient shape (kept for inline ad-hoc meal editing)
export interface Ingredient {
  name: string;
  quantity: number;
  unit: string;
}

export interface PlannedMeal {
  // Legacy single-name meal (backwards compat)
  name?: string;
  ingredients?: Ingredient[];
  // Structured fields
  items?: PlannedMealItem[];
  plannedServings?: number;
  guestIds?: string[];
  externalGuests?: ExternalGuest[];
  rsvps?: Record<string, 'confirmed' | 'abstained' | null>;
  notes?: string;
  leftoversFrom?: string;
}

export type MealSlot = 'breakfast' | 'lunch' | 'dinner' | 'snack';

export interface DayMeals {
  breakfast?: PlannedMeal;
  lunch?: PlannedMeal;
  dinner?: PlannedMeal;
  snack?: PlannedMeal;
}

export interface MealPlan {
  weekOf: string;
  days: Record<string, DayMeals>;
}

export interface GrocerySyncResult {
  added: number;
  removed: number;
  preview: Array<{ item: string; amount: string; unit: string }>;
}

export function useMealPlan(householdId: string | null, weekOf: string): {
  plan: MealPlan | null;
  loading: boolean;
  saveMeal: (day: string, slot: MealSlot, meal: PlannedMeal) => Promise<void>;
  deleteMeal: (day: string, slot: MealSlot) => Promise<void>;
  generateShoppingList: (recipes: Recipe[]) => Promise<GrocerySyncResult>;
  commitShoppingSync: (recipes: Recipe[]) => Promise<{ added: number; removed: number }>;
} {
  const [plan, setPlan] = useState<MealPlan | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!householdId) {
      setPlan(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    getDocs(collection(db, 'households', householdId, 'mealPlans'))
      .then((snapshot) => {
        const currentPlan = snapshot.docs.find(d => d.id === weekOf);
        if (currentPlan) {
          setPlan(currentPlan.data() as MealPlan);
        } else {
          setPlan({ weekOf, days: {} });
        }
      })
      .catch(() => setPlan({ weekOf, days: {} }))
      .finally(() => setLoading(false));
  }, [householdId, weekOf]);

  const saveMeal = useCallback(async (day: string, slot: MealSlot, meal: PlannedMeal): Promise<void> => {
    if (!householdId) return;
    const docRef = doc(db, 'households', householdId, 'mealPlans', weekOf);
    const currentDays = plan?.days || {};
    const updated: MealPlan = {
      weekOf,
      days: {
        ...currentDays,
        [day]: { ...currentDays[day], [slot]: meal },
      },
    };
    await setDoc(docRef, updated, { merge: true });
    setPlan(updated);
  }, [householdId, weekOf, plan]);

  const deleteMeal = useCallback(async (day: string, slot: MealSlot): Promise<void> => {
    if (!householdId || !plan) return;
    const docRef = doc(db, 'households', householdId, 'mealPlans', weekOf);
    const currentDays = { ...plan.days };
    if (currentDays[day]) {
      const dayMeals = { ...currentDays[day] };
      delete dayMeals[slot];
      currentDays[day] = dayMeals;
    }
    const updated: MealPlan = { weekOf, days: currentDays };
    await setDoc(docRef, updated);
    setPlan(updated);
  }, [householdId, weekOf, plan]);

  /** Compute the required grocery items for the current week without committing */
  const generateShoppingList = useCallback(async (recipes: Recipe[]): Promise<GrocerySyncResult> => {
    if (!householdId || !plan?.days) return { added: 0, removed: 0, preview: [] };

    // Count items to be removed (tagged with this week's sync)
    const inventorySnap = await getDocs(collection(db, 'households', householdId, 'tasks'));
    const toRemove = inventorySnap.docs.filter(d => {
      const data = d.data();
      return data.templateType === 'grocery' && data.mealPlanSync?.weekStart === weekOf;
    });

    // Collect and scale required ingredients
    const scaledMap = new Map<string, { item: string; amount: number; unit: string }>();

    Object.values(plan.days).forEach((dayMeals) => {
      (['breakfast', 'lunch', 'dinner', 'snack'] as MealSlot[]).forEach((slot) => {
        const meal = dayMeals[slot];
        if (!meal) return;

        const servings = meal.plannedServings ?? 2;
        const mealItems: PlannedMealItem[] = meal.items ?? (meal.name ? [{ recipeName: meal.name }] : []);

        mealItems.forEach((planItem) => {
          const recipe = recipes.find(r => r.id === planItem.recipeId);
          if (!recipe?.ingredients?.length) return;

          const scale = recipe.servings > 0 ? servings / recipe.servings : 1;
          recipe.ingredients.forEach((ing) => {
            const key = `${ing.item?.toLowerCase() || ''}_${ing.unit}`;
            const parsed = parseFloat(ing.amount) || 0;
            const scaled = parsed * scale;
            if (scaledMap.has(key)) {
              scaledMap.get(key)!.amount += scaled;
            } else {
              scaledMap.set(key, { item: ing.item, amount: scaled, unit: ing.unit });
            }
          });
        });

        // Also handle legacy inline ingredients on the meal itself
        if (meal.ingredients?.length) {
          meal.ingredients.forEach((ing) => {
            const key = `${ing.name.toLowerCase()}_${ing.unit}`;
            if (scaledMap.has(key)) {
              scaledMap.get(key)!.amount += ing.quantity;
            } else {
              scaledMap.set(key, { item: ing.name, amount: ing.quantity, unit: ing.unit });
            }
          });
        }
      });
    });

    const preview = Array.from(scaledMap.values()).map(v => ({
      item: v.item,
      amount: v.amount % 1 === 0 ? String(v.amount) : v.amount.toFixed(2),
      unit: v.unit,
    }));

    return { added: preview.length, removed: toRemove.length, preview };
  }, [householdId, plan, weekOf]);

  /** Actually write the delta sync to Firestore */
  const commitShoppingSync = useCallback(async (recipes: Recipe[]): Promise<{ added: number; removed: number }> => {
    if (!householdId || !plan?.days) return { added: 0, removed: 0 };

    const inventorySnap = await getDocs(collection(db, 'households', householdId, 'tasks'));
    const toRemove = inventorySnap.docs.filter(d => {
      const data = d.data();
      return data.templateType === 'grocery' && data.mealPlanSync?.weekStart === weekOf;
    });

    const { preview } = await generateShoppingList(recipes);

    const batch = writeBatch(db);

    // Remove stale items from this week
    toRemove.forEach(d => batch.delete(d.ref));

    // Add new items tagged with this week
    const inventoryRef = collection(db, 'households', householdId, 'tasks');
    preview.forEach((ing) => {
      const ref = doc(inventoryRef);
      batch.set(ref, {
        title: `${ing.amount} ${ing.unit} ${ing.item}`.trim(),
        templateType: 'grocery',
        status: 'todo',
        scope: 'household',
        householdId,
        mealPlanSync: { weekStart: weekOf },
        addedBy: 'meal-planner',
        createdAt: serverTimestamp(),
      });
    });

    await batch.commit();
    return { added: preview.length, removed: toRemove.length };
  }, [householdId, plan, weekOf, generateShoppingList]);

  return { plan, loading, saveMeal, deleteMeal, generateShoppingList, commitShoppingSync };
}
